import Badge from '../../components/ui/Badge/Badge.jsx';
import Button from '../../components/ui/Button/Button.jsx';
import Card from '../../components/ui/Card/Card.jsx';
import Input from '../../components/ui/Input/Input.jsx';
import SectionHeading from '../../components/ui/SectionHeading/SectionHeading.jsx';
import styles from './DesignSystem.module.css';

const colors = [
  { name: 'Background', value: '#0B0C12', var: '--color-bg' },
  { name: 'Surface', value: '#15172A', var: '--color-surface-solid' },
  { name: 'Border', value: '#2A2D45', var: '--color-border' },
  { name: 'Text', value: '#F4F5F9', var: '--color-text' },
  { name: 'Text Muted', value: '#9599B3', var: '--color-text-muted' },
  { name: 'Accent', value: '#7C6CFF', var: '--color-accent' },
  { name: 'Secondary', value: '#4FE3D4', var: '--color-secondary' },
  { name: 'Success', value: '#4ADE80', var: '--color-success' },
  { name: 'Warning', value: '#FBBF67', var: '--color-warning' },
  { name: 'Danger', value: '#FF6B6B', var: '--color-danger' },
];

const typeScale = [
  { label: 'Display XL', className: styles.display, sample: 'Aa', size: 'clamp(2.75rem, 5vw + 1rem, 5.25rem)' },
  { label: 'Display LG', className: styles.displayLg, sample: 'Aa', size: 'clamp(2.25rem, 3.4vw + 1rem, 3.5rem)' },
  { label: 'Display MD', className: styles.displayMd, sample: 'Aa', size: 'clamp(1.75rem, 2vw + 1rem, 2.5rem)' },
  { label: 'Body Large', className: styles.bodyLg, sample: 'Aa', size: '1.125rem' },
  { label: 'Body', className: styles.body, sample: 'Aa', size: '1rem' },
  { label: 'Caption / Mono', className: styles.caption, sample: 'Aa', size: '0.8125rem' },
];

const spacing = ['3xs', '2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'];

export default function DesignSystem() {
  return (
    <section className={styles.section}>
      <div className="container">
        <Badge tone="secondary">Design System</Badge>
        <h1 className={styles.title}>Every token and component, in one place</h1>
        <p className={styles.lead}>
          Solvora is built on a small set of shared values. Change one here, and it
          propagates through every page automatically.
        </p>

        <SectionHeading eyebrow="Color" title="Palette" />
        <div className={styles.colorGrid}>
          {colors.map((c) => (
            <div key={c.name} className={styles.colorItem}>
              <div className={styles.swatch} style={{ background: c.value }} />
              <span className={styles.colorName}>{c.name}</span>
              <span className={styles.colorValue}>{c.value}</span>
              <span className={styles.colorVar}>{c.var}</span>
            </div>
          ))}
        </div>

        <SectionHeading eyebrow="Type" title="Type scale" description="Space Grotesk for display, Inter for body, JetBrains Mono for labels and data." />
        <div className={styles.typeList}>
          {typeScale.map((t) => (
            <div key={t.label} className={styles.typeRow}>
              <span className={styles.typeSample} style={{ fontSize: t.size }}>{t.sample}</span>
              <div>
                <span className={styles.typeLabel}>{t.label}</span>
                <span className={styles.typeSize}>{t.size}</span>
              </div>
            </div>
          ))}
        </div>

        <SectionHeading eyebrow="Space" title="Spacing scale" />
        <div className={styles.spaceRow}>
          {spacing.map((s) => (
            <div key={s} className={styles.spaceItem}>
              <div className={styles.spaceBar} style={{ width: `var(--space-${s})` }} />
              <span className={styles.spaceLabel}>{s}</span>
            </div>
          ))}
        </div>

        <SectionHeading eyebrow="Components" title="Buttons" />
        <div className={styles.componentRow}>
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
        </div>

        <SectionHeading eyebrow="Components" title="Badges" />
        <div className={styles.componentRow}>
          <Badge tone="accent">Accent</Badge>
          <Badge tone="secondary">Secondary</Badge>
          <Badge tone="neutral">Neutral</Badge>
        </div>

        <SectionHeading eyebrow="Components" title="Cards & inputs" />
        <div className={styles.compGrid}>
          <Card>
            <h3 className={styles.cardTitle}>Card title</h3>
            <p className={styles.cardText}>Cards use a translucent surface with a soft border and lift on hover.</p>
          </Card>
          <Card hover={false}>
            <Input id="ds-input" label="Label" placeholder="Placeholder text" />
          </Card>
        </div>
      </div>
    </section>
  );
}
